import React from 'react'
import useFormLoader from '../../../core/hooks/useFormLoader'
import useSubmissionLoader from '../../../core/hooks/useSubmission'
import { IForm } from '../../../domain/interfaces/IForm'
import { ISubmission } from '../../../domain/interfaces/ISubmission'
import MojoFormioRenderer from './MojoFormioRenderer'
import InnerPageContainer from '../../components/inner-page-container/InnerPageContainer'
import { Box } from '@mui/material'

interface IMojoFormRenderProps {
  formId: string
  orgId: string
  form?: IForm
  submissionId?: string
  submission?: ISubmission
  isAnon?: boolean
  isDraft?: boolean
  callback?: () => void
  buttonGroup?: boolean
  noContainer?: boolean
  parentSubId?: string
  clearSubmissionStorage?: any
}

// This component handles the various potential states of setting up a formio form.  Starts by making sure that the form is loaded, then the submission data is loaded/setup for new submission, then loads the formio form.  Handles both anonymous and auth submissions
export const MojoFormRender1: React.FC<IMojoFormRenderProps> = ({
  formId,
  orgId,
  form,
  submission,
  submissionId,
  callback,
  isAnon = false,
  isDraft = false,
  noContainer = false,
  buttonGroup = false,
  parentSubId,
  clearSubmissionStorage,
}) => {
  //first check if there is a form if not load that
  if (!form) {
    return (
      <MojoFormLoader
        formId={formId}
        orgId={orgId}
        submissionId={submissionId}
        isAnon={isAnon}
        isDraft={isDraft}
        noContainer={noContainer}
        buttonGroup={buttonGroup}
        callback={callback}
        parentSubId={parentSubId}
        clearSubmissionStorage={clearSubmissionStorage}
      />
    )
  }
  if (!submission) {
    return (
      <MojoSubmissionLoader
        form={form}
        formId={formId}
        orgId={orgId}
        submissionId={submissionId}
        isAnon={isAnon}
        isDraft={isDraft}
        noContainer={noContainer}
        buttonGroup={buttonGroup}
        callback={callback}
        parentSubId={parentSubId}
        clearSubmissionStorage={clearSubmissionStorage}
      />
    )
  }
  return (
    <MojoFormioRenderer
      form={form!}
      submission={submission!}
      submissionId={submissionId}
      formId={formId}
      orgId={orgId}
      isAnon={isAnon}
      isDraft={isDraft}
      submissionFormatted={true}
      noContainer={noContainer}
      buttonGroup={buttonGroup}
      callback={callback}
      parentSubId={parentSubId}
      clearSubmissionStorage={clearSubmissionStorage}
    />
  )
}

interface IMojoFormLoaderProps {
  formId: string
  orgId: string
  submissionId?: string
  isAnon?: boolean
  isDraft?: boolean
  buttonGroup?: boolean
  noContainer?: boolean
  callback?: () => void
  parentSubId?: string
  clearSubmissionStorage?: any
}

const MojoFormLoader: React.FC<IMojoFormLoaderProps> = ({
  formId,
  orgId,
  submissionId,
  isAnon = false,
  isDraft = false,
  noContainer = false,
  buttonGroup = false,
  callback,
  parentSubId,
  clearSubmissionStorage,
}) => {
  const { form, loadFormError, formLoading } = useFormLoader(formId, {
    orgId,
    isAnon: isAnon,
  })
  if (formLoading) {
    return <p>Loading Form</p>
  }
  if (loadFormError) {
    return <p>Error Loading Form</p>
  }
  if (!form) {
    return <p>No form</p>
  }
  return (
    <InnerPageContainer
      collapsible
      hide={form!.currentUserPermission!.read == 0 || (form!.currentUserPermission!.create == 0 && !submissionId)}
      headerTitle={form.title}
      key={form.id + '-' + submissionId}
      sx={{ marginTop: '20px', marginBottom: '20px' }}
    >
      <Box sx={{ mt: '24px' }} />
      <MojoSubmissionLoader
        form={form}
        formId={formId}
        orgId={orgId}
        isAnon={isAnon}
        submissionId={submissionId}
        isDraft={isDraft}
        noContainer={noContainer}
        buttonGroup={buttonGroup}
        callback={callback}
        parentSubId={parentSubId}
        clearSubmissionStorage={clearSubmissionStorage}
      />
    </InnerPageContainer>
  )
}

interface IMojoSubmissionLoaderProps {
  formId: string
  orgId: string
  form: IForm
  submissionId?: string
  isAnon?: boolean
  isDraft?: boolean
  buttonGroup?: boolean
  noContainer?: boolean
  callback?: () => void
  parentSubId?: string
  clearSubmissionStorage?: any
}

const MojoSubmissionLoader: React.FC<IMojoSubmissionLoaderProps> = ({
  formId,
  orgId,
  form,
  submissionId,
  callback,
  isAnon = false,
  isDraft = false,
  buttonGroup = false,
  noContainer = false,
  parentSubId,
  clearSubmissionStorage,
}) => {
  const { submission, submissionFormatted, submissionLoading, loadSubmissionError } = useSubmissionLoader(submissionId)
  if (loadSubmissionError) {
    return <p>Error Loading Submission</p>
  }
  if (submissionLoading || !submissionFormatted) {
    return <p>Loading Submission</p>
  }

  if (!submission) {
    return <p>No submission</p>
  }
  return (
    <MojoFormioRenderer
      submissionFormatted={submissionFormatted}
      form={form}
      submission={submission}
      formId={formId}
      orgId={orgId}
      isAnon={isAnon}
      isDraft={isDraft}
      buttonGroup={buttonGroup}
      noContainer={noContainer}
      callback={callback}
      parentSubId={parentSubId}
      clearSubmissionStorage={clearSubmissionStorage}
    />
  )
}
